import { sortByName } from '../utils/sortOptions';
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon, MagnifyingGlassIcon, ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

// ── Export helpers ────────────────────────────────────────────────────────────
const exportCSV = (rows, filename) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape  = v => { const s = v == null ? '' : String(v); return s.includes(',') || s.includes('"') ? '"' + s.replace(/"/g,'""') + '"' : s; };
  const csv = [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type:'text/csv' }));
  a.download = filename + '.csv'; a.click();
};
const exportData = async (rows, filename, fmt) => {
  if (!rows?.length) return;
  if (fmt === 'csv') { exportCSV(rows, filename); return; }
  try {
    const XLSX = await import('xlsx');
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Sheet1');
    XLSX.writeFile(wb, filename + '.xlsx');
  } catch { exportCSV(rows, filename); }
};

const STATUS_OPTS  = ['present','absent','on-leave','holiday','half-day'];
const STATUS_COLOR = { present:'#10c98e', absent:'#ef4444', 'on-leave':'#f59e0b', holiday:'#9094a8', 'half-day':'#3b6ef8' };
const STATUS_BG    = { present:'#ecfdf5', absent:'#fef2f2', 'on-leave':'#fffbeb', holiday:'#f0f2f7', 'half-day':'#eef2ff' };
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const initials = n => (n||'').split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?';
const fmtTime  = d => d ? new Date(d).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true}) : '—';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .sa * { box-sizing:border-box; }
  .sa { font-family:'Sora',sans-serif; color:#1a1d2e; animation:saFade .4s ease both; }
  @keyframes saFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .sa-title { font-size:1.4rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; margin-bottom:3px; }
  .sa-sub   { font-size:.76rem; color:#9094a8; margin-bottom:1.5rem; }
  .sa-toolbar { display:flex; gap:.7rem; flex-wrap:wrap; align-items:center; margin-bottom:1.2rem; }
  .sa-date { font-family:'JetBrains Mono',monospace; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; outline:none; }
  .sa-date:focus { border-color:#3b6ef8; }
  .sa-search-wrap { position:relative; flex:1; max-width:280px; }
  .sa-search-wrap svg { position:absolute; left:11px; top:50%; transform:translateY(-50%); width:14px; height:14px; stroke:#9094a8; pointer-events:none; }
  .sa-search { font-family:'Sora',sans-serif; font-size:.8rem; width:100%; padding:8px 12px 8px 32px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; outline:none; }
  .sa-search:focus { border-color:#3b6ef8; }
  .view-tabs { display:flex; gap:5px; }
  .view-btn { padding:7px 14px; border-radius:9px; border:1.5px solid #e0e4f0; background:white; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; }
  .view-btn.active { background:#3b6ef8; color:white; border-color:#3b6ef8; }
  .export-group { display:flex; gap:5px; margin-left:auto; align-items:center; }
  .btn-export { display:inline-flex; align-items:center; gap:5px; padding:7px 14px; border:1.5px solid #e0e4f0; border-radius:9px; background:white; font-family:'Sora',sans-serif; font-size:.76rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; }
  .btn-export svg { width:14px; height:14px; stroke-width:2; }
  .btn-export.csv:hover  { background:#ecfdf5; color:#10c98e; border-color:#10c98e; }
  .btn-export.xlsx:hover { background:#eef2ff; color:#3b6ef8; border-color:#3b6ef8; }
  .btn-save-all { display:inline-flex; align-items:center; gap:6px; padding:8px 16px; border:none; border-radius:10px; background:#3b6ef8; color:white; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; }
  .btn-save-all svg { width:14px; height:14px; stroke-width:2.5; }
  .sa-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .sa-tbl-wrap { overflow-x:auto; }
  .sa-tbl { width:100%; border-collapse:collapse; }
  .sa-tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .sa-tbl th { padding:10px 14px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .sa-tbl tbody tr { border-bottom:1px solid #f3f5fb; transition:background .1s; }
  .sa-tbl tbody tr:last-child { border-bottom:none; }
  .sa-tbl tbody tr:hover { background:#fafbff; }
  .sa-tbl td { padding:10px 14px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .sa-avatar { width:34px; height:34px; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:.72rem; font-weight:700; color:white; flex-shrink:0; background:linear-gradient(135deg,#3b6ef8,#8b5cf6); }
  .status-sel { font-family:'Sora',sans-serif; font-size:.74rem; font-weight:600; padding:4px 8px; border-radius:8px; border:1.5px solid; cursor:pointer; outline:none; }
  .time-input { font-family:'JetBrains Mono',monospace; font-size:.74rem; padding:4px 8px; border:1.5px solid #e0e4f0; border-radius:7px; background:#f8f9fc; outline:none; width:90px; }
  .time-input:focus { border-color:#3b6ef8; background:white; }
  .late-input { font-family:'JetBrains Mono',monospace; font-size:.74rem; padding:4px 6px; border:1.5px solid #e0e4f0; border-radius:7px; background:#f8f9fc; outline:none; width:60px; }
  .btn-save-row { padding:5px 12px; border:none; border-radius:8px; background:#3b6ef8; color:white; font-family:'Sora',sans-serif; font-size:.72rem; font-weight:600; cursor:pointer; white-space:nowrap; }
  .btn-save-row:disabled { opacity:.5; cursor:not-allowed; }
  .stat-pill { font-size:.68rem; font-weight:700; padding:3px 10px; border-radius:99px; display:inline-flex; white-space:nowrap; }
  .yr-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:1rem; }
  .yr-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1.1rem 1.2rem; }
  .yr-card-name { font-size:.88rem; font-weight:700; color:#0f1123; margin-bottom:.8rem; display:flex; align-items:center; gap:9px; }
  .yr-months { display:grid; grid-template-columns:repeat(6,1fr); gap:4px; }
  .yr-month { text-align:center; }
  .yr-month-lbl { font-size:.58rem; font-weight:600; color:#9094a8; text-transform:uppercase; margin-bottom:3px; }
  .yr-month-bar { height:32px; border-radius:6px; display:flex; align-items:center; justify-content:center; font-size:.62rem; font-weight:700; }
  .yr-totals { display:flex; gap:8px; flex-wrap:wrap; margin-top:.8rem; padding-top:.7rem; border-top:1px solid #f0f2f7; }
  .yr-total { font-size:.68rem; font-weight:600; }
  .sa-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .sa-spin { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
  .sa-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }

  /* ── Mobile cards ── */
  .sa-cards { display:none; padding:10px; }
  .sa-card  { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:12px 13px; margin-bottom:8px; }
  .sa-card-top { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; gap:8px; flex-wrap:wrap; }
  .sa-card-row { display:flex; justify-content:space-between; align-items:center; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .sa-card-row:last-child { border-bottom:none; }
  .sa-card-lbl { color:#9094a8; font-weight:600; white-space:nowrap; }
  .sa-card-actions { display:flex; gap:6px; margin-top:8px; flex-wrap:wrap; align-items:center; }

  @media (max-width:768px) {
    .sa-toolbar { flex-direction:column; align-items:stretch; }
    .sa-search-wrap { max-width:100%; }
    .export-group { margin-left:0; }
    .view-tabs { width:100%; }
    .view-btn { flex:1; text-align:center; }
    .sa-tbl-wrap { display:none !important; }
    .sa-cards { display:block; }
  }
`;

export default function OwnerAdminAttendance() {
  const [schools,   setSchools]   = useState([]);
  const [schoolId,  setSchoolId]  = useState('');
  const [view,      setView]      = useState('daily');
  const [date,      setDate]      = useState(new Date().toISOString().slice(0,10));
  const [month,     setMonth]     = useState(new Date().toISOString().slice(0,7));
  const [year,      setYear]      = useState(new Date().getFullYear());
  const [search,    setSearch]    = useState('');
  const [staff,     setStaff]     = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [forms,     setForms]     = useState({});
  const [saving,    setSaving]    = useState({});

  // Load schools once
  useEffect(() => {
    api.get('/owner/schools').then(r => {
      const s = r.data?.schools || r.data || [];
      setSchools(Array.isArray(s) ? s : []);
      if (s.length) setSchoolId(s[0]._id);
    }).catch(() => {});
  }, []);

  const fetchStaff = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      if (view === 'daily') {
        const { data } = await api.get(`/owner/branches/${schoolId}/admin-attendance?date=${date}`);
        const list = Array.isArray(data) ? data : [];
        setStaff(list);
        const f = {};
        list.forEach(a => {
          f[a._id] = {
            status:   a.attendance?.status || '',
            checkIn:  a.attendance?.checkIn  ? new Date(a.attendance.checkIn).toTimeString().slice(0,5)  : '',
            checkOut: a.attendance?.checkOut ? new Date(a.attendance.checkOut).toTimeString().slice(0,5) : '',
            lateMinutes: a.attendance?.lateMinutes || 0,
          };
        });
        setForms(f);
      } else if (view === 'monthly') {
        const { data } = await api.get(`/owner/branches/${schoolId}/admin-attendance/monthly?month=${month}`);
        setStaff(Array.isArray(data) ? data : []);
      } else {
        // Yearly: fetch base list then 12 months
        const base = await api.get(`/owner/branches/${schoolId}/admin-attendance?date=${year}-01-01`);
        const baseList = (Array.isArray(base.data) ? base.data : []).map(a => ({
          _id: a._id, name: a.name, email: a.email, months: {},
        }));
        const monthlyData = await Promise.all(
          Array.from({ length:12 }, (_, i) => {
            const m = String(i+1).padStart(2,'0');
            return api.get(`/owner/branches/${schoolId}/admin-attendance/monthly?month=${year}-${m}`)
              .then(r => ({ month: i+1, data: Array.isArray(r.data) ? r.data : [] }))
              .catch(() => ({ month: i+1, data: [] }));
          })
        );
        const results = baseList.map(a => {
          const entry = { ...a };
          monthlyData.forEach(({ month: m, data }) => {
            const found = data.find(d => d._id?.toString() === a._id?.toString());
            entry.months[m] = found || { present:0, absent:0, onLeave:0, lateCount:0, totalLateMinutes:0 };
          });
          return entry;
        });
        setStaff(results);
      }
    } catch { toast.error('Failed to load attendance'); }
    finally { setLoading(false); }
  }, [schoolId, view, date, month, year]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const setForm = (id, key, val) =>
    setForms(p => ({ ...p, [id]: { ...p[id], [key]: val } }));

  const saveSingle = async (adminId) => {
    const f = forms[adminId] || {};
    if (!f.status) return toast.error('Select a status first');
    setSaving(p => ({ ...p, [adminId]: true }));
    try {
      await api.post(`/owner/branches/${schoolId}/admin-attendance/mark`, {
        adminId, date,
        status:           f.status,
        checkIn:          f.checkIn  ? `${date}T${f.checkIn}:00`  : undefined,
        checkOut:         f.checkOut ? `${date}T${f.checkOut}:00` : undefined,
        lateMinutes:      Number(f.lateMinutes) || 0,
        earlyExitMinutes: 0,
      });
      toast.success('Saved');
      fetchStaff();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(p => ({ ...p, [adminId]: false })); }
  };

  const saveAll = async () => {
    const records = staff.filter(a => forms[a._id]?.status);
    if (!records.length) return toast.error('No attendance marked');
    let saved = 0, failed = 0;
    await Promise.all(records.map(async a => {
      const f = forms[a._id];
      try {
        await api.post(`/owner/branches/${schoolId}/admin-attendance/mark`, {
          adminId: a._id, date, status: f.status,
          checkIn:     f.checkIn  ? `${date}T${f.checkIn}:00`  : undefined,
          checkOut:    f.checkOut ? `${date}T${f.checkOut}:00` : undefined,
          lateMinutes: Number(f.lateMinutes) || 0,
        });
        saved++;
      } catch { failed++; }
    }));
    if (saved)  toast.success(`${saved} records saved`);
    if (failed) toast.error(`${failed} failed`);
    fetchStaff();
  };

  const handleExport = (fmt) => {
    if (!staff.length) return toast.error('No data to export');
    let rows = [];
    if (view === 'daily') {
      rows = staff.map(a => ({
        Name: a.name, Email: a.email, Date: date,
        Status: forms[a._id]?.status || a.attendance?.status || '',
        'Check In':  forms[a._id]?.checkIn  || '',
        'Check Out': forms[a._id]?.checkOut || '',
        'Late (min)': forms[a._id]?.lateMinutes || 0,
      }));
      exportData(rows, `Admin_Attendance_${date}`, fmt);
    } else if (view === 'monthly') {
      rows = staff.map(a => ({
        Name: a.name, Email: a.email, Month: month,
        Present: a.present||0, Absent: a.absent||0,
        'On Leave': a.onLeave||0, 'Late Mins': a.totalLateMinutes||0,
      }));
      exportData(rows, `Admin_Attendance_${month}`, fmt);
    } else {
      staff.forEach(a => {
        Array.from({length:12}, (_,i) => {
          const m = a.months?.[i+1] || {};
          rows.push({ Name: a.name, Month: `${year}-${String(i+1).padStart(2,'0')}`,
            Present: m.present||0, Absent: m.absent||0, 'On Leave': m.onLeave||0,
            'Late Mins': m.totalLateMinutes||0 });
        });
      });
      exportData(rows, `Admin_Attendance_${year}`, fmt);
    }
  };

  const exportSingle = (a, fmt) => {
    if (view === 'monthly') {
      exportData([{ Name:a.name, Month:month, Present:a.present||0, Absent:a.absent||0, 'On Leave':a.onLeave||0, 'Late Mins':a.totalLateMinutes||0 }], `Attendance_${a.name}_${month}`, fmt);
    } else if (view === 'yearly') {
      exportData(Array.from({length:12},(_,i)=>{ const m=a.months?.[i+1]||{}; return { Name:a.name, Month:`${year}-${String(i+1).padStart(2,'0')}`, Present:m.present||0, Absent:m.absent||0, 'On Leave':m.onLeave||0, 'Late Mins':m.totalLateMinutes||0 }; }), `Attendance_${a.name}_${year}`, fmt);
    } else {
      const f = forms[a._id] || {};
      exportData([{ Name:a.name, Date:date, Status:f.status||'', 'Check In':f.checkIn||'', 'Check Out':f.checkOut||'', 'Late (min)':f.lateMinutes||0 }], `Attendance_${a.name}_${date}`, fmt);
    }
  };

  const filtered = staff.filter(a => !search || a.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="sa"><style>{css}</style>

      {/* School selector */}
      <div style={{marginBottom:'1rem'}}>
        <select value={schoolId} onChange={e => setSchoolId(e.target.value)}
          style={{minWidth:220,padding:'9px 12px',border:'1.5px solid #e0e4f0',borderRadius:10,fontFamily:'Sora',fontSize:'.82rem',background:'white',outline:'none'}}>
          <option value="">Select School…</option>
          {sortByName(schools).map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
      </div>

      <div className="sa-title">Admin Attendance</div>
      <div className="sa-sub">Mark and view attendance for school admins — daily, monthly and yearly.</div>

      <div className="sa-toolbar">
        {view === 'daily'
          ? <input type="date"   className="sa-date" value={date}  onChange={e => setDate(e.target.value)} />
          : view === 'monthly'
          ? <input type="month"  className="sa-date" value={month} onChange={e => setMonth(e.target.value)} />
          : <input type="number" className="sa-date" value={year} min="2020" max="2099" onChange={e => setYear(Number(e.target.value))} style={{width:100}} />
        }
        <div className="sa-search-wrap">
          <MagnifyingGlassIcon />
          <input className="sa-search" placeholder="Search admins…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="view-tabs">
          {['daily','monthly','yearly'].map(v => (
            <button key={v} className={`view-btn ${view===v?'active':''}`} onClick={() => setView(v)}>
              {v.charAt(0).toUpperCase()+v.slice(1)}
            </button>
          ))}
        </div>
        {view === 'daily' && <button className="btn-save-all" onClick={saveAll}><CheckCircleIcon />Save All</button>}
        <div className="export-group">
          <button className="btn-export csv"  onClick={() => handleExport('csv')}><ArrowDownTrayIcon />CSV</button>
          <button className="btn-export xlsx" onClick={() => handleExport('xlsx')}><ArrowDownTrayIcon />XLSX</button>
        </div>
      </div>

      {loading ? (
        <div className="sa-loading"><div className="sa-spin"/>Loading…</div>
      ) : !schoolId ? (
        <div className="sa-panel"><div className="sa-empty">Select a school to view attendance.</div></div>
      ) : filtered.length === 0 ? (
        <div className="sa-panel"><div className="sa-empty">No admins found.</div></div>
      ) : view === 'daily' ? (
        <div className="sa-panel">
          <div className="sa-tbl-wrap">
            <table className="sa-tbl">
              <thead><tr>
                <th>Admin</th><th>Status</th><th>Check In</th><th>Check Out</th><th>Late (min)</th><th>Action</th>
              </tr></thead>
              <tbody>
                {filtered.map(a => {
                  const f  = forms[a._id] || {};
                  const sc = STATUS_COLOR[f.status] || '#9094a8';
                  const sb = STATUS_BG[f.status]    || '#f0f2f7';
                  return (
                    <tr key={a._id}>
                      <td>
                        <div style={{display:'flex',alignItems:'center',gap:9}}>
                          <div className="sa-avatar">{initials(a.name)}</div>
                          <div>
                            <div style={{fontWeight:600,color:'#0f1123'}}>{a.name}</div>
                            <div style={{fontSize:'.68rem',color:'#9094a8'}}>{a.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <select className="status-sel" style={{background:sb,color:sc,borderColor:sc}}
                          value={f.status||''} onChange={e => setForm(a._id,'status',e.target.value)}>
                          <option value="">— select —</option>
                          {STATUS_OPTS.map(st => <option key={st} value={st}>{st}</option>)}
                        </select>
                      </td>
                      <td>
                        {(f.status==='present'||f.status==='half-day')
                          ? <input type="time" className="time-input" value={f.checkIn||''} onChange={e => setForm(a._id,'checkIn',e.target.value)} />
                          : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>—</span>}
                      </td>
                      <td>
                        {(f.status==='present'||f.status==='half-day')
                          ? <input type="time" className="time-input" value={f.checkOut||''} onChange={e => setForm(a._id,'checkOut',e.target.value)} />
                          : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>—</span>}
                      </td>
                      <td>
                        {f.status==='present'
                          ? <input type="number" min="0" className="late-input" value={f.lateMinutes||0} onChange={e => setForm(a._id,'lateMinutes',e.target.value)} />
                          : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>—</span>}
                      </td>
                      <td>
                        <div style={{display:'flex',gap:4}}>
                          <button className="btn-save-row" disabled={saving[a._id]} onClick={() => saveSingle(a._id)}>
                            {saving[a._id] ? '…' : 'Save'}
                          </button>
                          <button onClick={() => exportSingle(a,'csv')}
                            style={{padding:'5px 8px',border:'1.5px solid #e0e4f0',borderRadius:7,background:'white',cursor:'pointer',fontSize:'.68rem',fontWeight:600,color:'#9094a8',fontFamily:'Sora'}}>
                            CSV
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="sa-cards">
            {filtered.map(a => {
              const f  = forms[a._id] || {};
              const sc = STATUS_COLOR[f.status] || '#9094a8';
              const sb = STATUS_BG[f.status]    || '#f0f2f7';
              return (
                <div key={a._id} className="sa-card">
                  <div className="sa-card-top">
                    <div style={{display:'flex',alignItems:'center',gap:9}}>
                      <div className="sa-avatar">{initials(a.name)}</div>
                      <div>
                        <div style={{fontWeight:600,color:'#0f1123'}}>{a.name}</div>
                        <div style={{fontSize:'.68rem',color:'#9094a8'}}>{a.email}</div>
                      </div>
                    </div>
                    <select className="status-sel" style={{background:sb,color:sc,borderColor:sc}}
                      value={f.status||''} onChange={e => setForm(a._id,'status',e.target.value)}>
                      <option value="">— select —</option>
                      {STATUS_OPTS.map(st => <option key={st} value={st}>{st}</option>)}
                    </select>
                  </div>
                  <div className="sa-card-row">
                    <span className="sa-card-lbl">Check In</span>
                    {(f.status==='present'||f.status==='half-day')
                      ? <input type="time" className="time-input" value={f.checkIn||''} onChange={e => setForm(a._id,'checkIn',e.target.value)} />
                      : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>—</span>}
                  </div>
                  <div className="sa-card-row">
                    <span className="sa-card-lbl">Check Out</span>
                    {(f.status==='present'||f.status==='half-day')
                      ? <input type="time" className="time-input" value={f.checkOut||''} onChange={e => setForm(a._id,'checkOut',e.target.value)} />
                      : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>—</span>}
                  </div>
                  <div className="sa-card-row">
                    <span className="sa-card-lbl">Late (min)</span>
                    {f.status==='present'
                      ? <input type="number" min="0" className="late-input" value={f.lateMinutes||0} onChange={e => setForm(a._id,'lateMinutes',e.target.value)} />
                      : <span style={{color:'#c0c5dc',fontSize:'.74rem'}}>—</span>}
                  </div>
                  <div className="sa-card-actions">
                    <button className="btn-save-row" disabled={saving[a._id]} onClick={() => saveSingle(a._id)}>
                      {saving[a._id] ? '…' : 'Save'}
                    </button>
                    <button onClick={() => exportSingle(a,'csv')}
                      style={{padding:'5px 8px',border:'1.5px solid #e0e4f0',borderRadius:7,background:'white',cursor:'pointer',fontSize:'.68rem',fontWeight:600,color:'#9094a8',fontFamily:'Sora'}}>
                      CSV
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      ) : view === 'monthly' ? (
        <div className="sa-panel">
          <div className="sa-tbl-wrap">
            <table className="sa-tbl">
              <thead><tr>
                <th>Admin</th><th>Present</th><th>Absent</th><th>On Leave</th><th>Late Days</th><th>Late Mins</th><th>Export</th>
              </tr></thead>
              <tbody>
                {filtered.map(a => (
                  <tr key={a._id}>
                    <td>
                      <div style={{display:'flex',alignItems:'center',gap:9}}>
                        <div className="sa-avatar">{initials(a.name)}</div>
                        <div style={{fontWeight:600,color:'#0f1123'}}>{a.name}</div>
                      </div>
                    </td>
                    <td><span className="stat-pill" style={{background:'#ecfdf5',color:'#10c98e'}}>{a.present||0}</span></td>
                    <td><span className="stat-pill" style={{background:'#fef2f2',color:'#ef4444'}}>{a.absent||0}</span></td>
                    <td><span className="stat-pill" style={{background:'#fffbeb',color:'#f59e0b'}}>{a.onLeave||0}</span></td>
                    <td style={{fontSize:'.78rem',color:'#6b7194'}}>{a.lateCount||0}</td>
                    <td style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.78rem',color:'#ef4444'}}>{a.totalLateMinutes||0} min</td>
                    <td>
                      <div style={{display:'flex',gap:3}}>
                        <button onClick={() => exportSingle(a,'csv')}  style={{padding:'4px 7px',border:'1.5px solid #e0e4f0',borderRadius:6,background:'white',cursor:'pointer',fontSize:'.66rem',fontWeight:600,color:'#9094a8',fontFamily:'Sora'}}>CSV</button>
                        <button onClick={() => exportSingle(a,'xlsx')} style={{padding:'4px 7px',border:'1.5px solid #3b6ef8',borderRadius:6,background:'#eef2ff',cursor:'pointer',fontSize:'.66rem',fontWeight:600,color:'#3b6ef8',fontFamily:'Sora'}}>XLSX</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="sa-cards">
            {filtered.map(a => (
              <div key={a._id} className="sa-card">
                <div className="sa-card-top">
                  <div style={{display:'flex',alignItems:'center',gap:9}}>
                    <div className="sa-avatar">{initials(a.name)}</div>
                    <div style={{fontWeight:600,color:'#0f1123'}}>{a.name}</div>
                  </div>
                </div>
                <div className="sa-card-row"><span className="sa-card-lbl">Present</span><span className="stat-pill" style={{background:'#ecfdf5',color:'#10c98e'}}>{a.present||0}</span></div>
                <div className="sa-card-row"><span className="sa-card-lbl">Absent</span><span className="stat-pill" style={{background:'#fef2f2',color:'#ef4444'}}>{a.absent||0}</span></div>
                <div className="sa-card-row"><span className="sa-card-lbl">On Leave</span><span className="stat-pill" style={{background:'#fffbeb',color:'#f59e0b'}}>{a.onLeave||0}</span></div>
                <div className="sa-card-row"><span className="sa-card-lbl">Late Days</span><span style={{fontSize:'.78rem',color:'#6b7194'}}>{a.lateCount||0}</span></div>
                <div className="sa-card-row"><span className="sa-card-lbl">Late Mins</span><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.78rem',color:'#ef4444'}}>{a.totalLateMinutes||0} min</span></div>
                <div className="sa-card-actions">
                  <button onClick={() => exportSingle(a,'csv')}  style={{padding:'4px 7px',border:'1.5px solid #e0e4f0',borderRadius:6,background:'white',cursor:'pointer',fontSize:'.66rem',fontWeight:600,color:'#9094a8',fontFamily:'Sora'}}>CSV</button>
                  <button onClick={() => exportSingle(a,'xlsx')} style={{padding:'4px 7px',border:'1.5px solid #3b6ef8',borderRadius:6,background:'#eef2ff',cursor:'pointer',fontSize:'.66rem',fontWeight:600,color:'#3b6ef8',fontFamily:'Sora'}}>XLSX</button>
                </div>
              </div>
            ))}
          </div>
        </div>

      ) : (
        // Yearly grid
        <div className="yr-grid">
          {filtered.map(a => {
            const totals = Object.values(a.months||{}).reduce((acc,m) => ({
              present: acc.present+(m.present||0),
              absent:  acc.absent+(m.absent||0),
              onLeave: acc.onLeave+(m.onLeave||0),
              lateMin: acc.lateMin+(m.totalLateMinutes||0),
            }), {present:0,absent:0,onLeave:0,lateMin:0});
            return (
              <div key={a._id} className="yr-card">
                <div className="yr-card-name">
                  <div className="sa-avatar" style={{width:30,height:30,fontSize:'.66rem'}}>{initials(a.name)}</div>
                  {a.name}
                  <span style={{fontSize:'.68rem',color:'#9094a8',marginLeft:'auto'}}>{a.email}</span>
                </div>
                <div className="yr-months">
                  {MONTHS.map((lbl,i) => {
                    const m = a.months?.[i+1] || {};
                    const total = (m.present||0)+(m.absent||0)+(m.onLeave||0);
                    const pct   = total > 0 ? Math.round(((m.present||0)/total)*100) : 0;
                    const bg  = pct>=90?'#ecfdf5':pct>=70?'#fffbeb':pct>0?'#fef2f2':'#f0f2f7';
                    const col = pct>=90?'#10c98e':pct>=70?'#f59e0b':pct>0?'#ef4444':'#c0c5dc';
                    return (
                      <div key={lbl} className="yr-month">
                        <div className="yr-month-lbl">{lbl}</div>
                        <div className="yr-month-bar" style={{background:bg,color:col}}>{total>0?`${pct}%`:'—'}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="yr-totals">
                  <span className="yr-total" style={{color:'#10c98e'}}>✓ {totals.present}</span>
                  <span className="yr-total" style={{color:'#ef4444'}}>✗ {totals.absent}</span>
                  <span className="yr-total" style={{color:'#f59e0b'}}>◑ {totals.onLeave}</span>
                  <span className="yr-total" style={{color:'#9094a8'}}>⏱ {totals.lateMin}m</span>
                  <span style={{marginLeft:'auto',display:'flex',gap:3}}>
                    <button onClick={() => exportSingle(a,'csv')}  style={{padding:'3px 7px',border:'1.5px solid #e0e4f0',borderRadius:6,background:'white',cursor:'pointer',fontSize:'.64rem',fontWeight:600,color:'#9094a8',fontFamily:'Sora'}}>CSV</button>
                    <button onClick={() => exportSingle(a,'xlsx')} style={{padding:'3px 7px',border:'1.5px solid #3b6ef8',borderRadius:6,background:'#eef2ff',cursor:'pointer',fontSize:'.64rem',fontWeight:600,color:'#3b6ef8',fontFamily:'Sora'}}>XLSX</button>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}